import { useState } from 'react';
import type {
  PlantillaResumen,
  AreaMantenimiento,

  FrecuenciaMantenimiento,
  Oficio,
  Proveedor,
  TipoDocumento,
} from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { Modal } from './Modal.js';
import { Icono } from './Iconos.js';

const AREAS: AreaMantenimiento[] = ['INTERIOR', 'EXTERIOR', 'SISTEMAS', 'PISCINA', 'JARDIN', 'OTRO'];
const FRECUENCIAS: FrecuenciaMantenimiento[] = [
  'SEMANAL',
  'QUINCENAL',
  'MENSUAL',
  'TRIMESTRAL',
  'SEMESTRAL',
  'ANUAL',
  'PERSONALIZADA',
];
const OFICIOS: Oficio[] = [
  'FONTANERIA',
  'JARDINERIA',
  'CLIMA',
  'PISCINAS',
  'LIMPIEZA',
  'ELECTRICIDAD',
  'OBRA',
  'SEGURIDAD',
  'OTRO',
];
const TIPOS_DOC: TipoDocumento[] = ['CONTRATO', 'SEGURO', 'GARANTIA', 'FACTURA', 'OTRO'];

const hoy = () => new Date().toISOString().slice(0, 10);

// --- Nueva tarea de mantenimiento --------------------------------------------

export function ModalMantenimiento({
  idioma,
  propiedadId,
  proveedores,
  onHecho,
  onCerrar,
}: {
  idioma: Idioma;
  propiedadId: string;
  proveedores: Proveedor[];
  onHecho: () => void;
  onCerrar: () => void;
}) {
  const [concepto, setConcepto] = useState('');
  const [area, setArea] = useState<AreaMantenimiento>('OTRO');
  const [frecuencia, setFrecuencia] = useState<FrecuenciaMantenimiento>('MENSUAL');
  const [antelacion, setAntelacion] = useState('7');
  const [proxima, setProxima] = useState(hoy());
  const [genera, setGenera] = useState<'VISITA' | 'INCIDENCIA'>('VISITA');
  const [providerId, setProviderId] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guardar = (e: React.FormEvent) => {
    e.preventDefault();
    if (concepto.trim().length < 2) return;
    setEnviando(true);
    api
      .crearMantenimiento(propiedadId, {
        concepto: concepto.trim(),
        area,
        frecuencia,
        genera,
        diasAntelacion: Number(antelacion) || 0,
        ...(proxima ? { proximaEn: proxima } : {}),
        ...(providerId ? { providerId } : {}),
      })
      .then(() => {
        onHecho();
        onCerrar();
      })
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
      )
      .finally(() => setEnviando(false));
  };

  return (
    <Modal idioma={idioma} titulo={t(idioma, 'mant.nueva')} icono="calendario" onCerrar={onCerrar}>
      <form className="formulario" style={{ maxWidth: 'none' }} onSubmit={guardar}>
        <div className="campo">
          <label htmlFor="m-concepto">{t(idioma, 'mant.titulo')}</label>
          <input
            id="m-concepto"
            type="text"
            value={concepto}
            onChange={(e) => setConcepto(e.target.value)}
            maxLength={300}
            required
          />
        </div>
        <div className="campo">
          <label htmlFor="m-area">{t(idioma, 'mant.area')}</label>
          <select id="m-area" value={area} onChange={(e) => setArea(e.target.value as AreaMantenimiento)}>
            {AREAS.map((a) => (
              <option key={a} value={a}>
                {t(idioma, `areaMant.${a}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="rejilla dos">
          <div className="campo">
            <label htmlFor="m-frec">{t(idioma, 'mant.frecuencia')}</label>
            <select
              id="m-frec"
              value={frecuencia}
              onChange={(e) => setFrecuencia(e.target.value as FrecuenciaMantenimiento)}
            >
              {FRECUENCIAS.map((f) => (
                <option key={f} value={f}>
                  {t(idioma, `frecuencia.${f}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="m-ant">{t(idioma, 'mant.antelacion')}</label>
            <input
              id="m-ant"
              type="number"
              min={0}
              max={365}
              value={antelacion}
              onChange={(e) => setAntelacion(e.target.value)}
            />
          </div>
        </div>
        <div className="campo">
          <label htmlFor="m-prox">{t(idioma, 'mant.fechaInicio')}</label>
          <input id="m-prox" type="date" value={proxima} onChange={(e) => setProxima(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="m-genera">{t(idioma, 'ficha.genera')}</label>
          <select
            id="m-genera"
            value={genera}
            onChange={(e) => setGenera(e.target.value as 'VISITA' | 'INCIDENCIA')}
          >
            <option value="VISITA">{t(idioma, 'mant.comoVisita')}</option>
            <option value="INCIDENCIA">{t(idioma, 'mant.comoIncidencia')}</option>
          </select>
        </div>
        <div className="campo">
          <label htmlFor="m-prov">{t(idioma, 'mant.proveedorOpcional')}</label>
          <select id="m-prov" value={providerId} onChange={(e) => setProviderId(e.target.value)}>
            <option value="">— {t(idioma, 'evento.sinAsignar')} —</option>
            {proveedores
              .filter((p) => p.activo)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
          </select>
        </div>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="acceso-entrar" disabled={enviando || concepto.trim().length < 2}>
          + {t(idioma, 'mant.guardar')}
        </button>
      </form>
    </Modal>
  );
}

// --- Nuevo proveedor ----------------------------------------------------------

export function ModalProveedor({
  idioma,
  propiedadId,
  onHecho,
  onCerrar,
}: {
  idioma: Idioma;
  /** Si viene, el proveedor recién creado se vincula a esa propiedad. */
  propiedadId?: string;
  onHecho: () => void;
  onCerrar: () => void;
}) {
  const [nombre, setNombre] = useState('');
  const [oficio, setOficio] = useState<Oficio>('OTRO');
  const [contacto, setContacto] = useState('');
  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [nif, setNif] = useState('');
  const [zona, setZona] = useState('');
  const [notas, setNotas] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guardar = (e: React.FormEvent) => {
    e.preventDefault();
    if (nombre.trim().length < 2) return;
    setEnviando(true);
    // El contacto y las notas van juntos al campo de notas: el modelo guarda una
    // empresa, no una agenda de personas. Se dice, en vez de inventar columnas.
    const notasCompletas = [contacto.trim() ? `Contacto: ${contacto.trim()}` : '', notas.trim()]
      .filter(Boolean)
      .join('\n');
    api
      .crearProveedor({
        nombre: nombre.trim(),
        oficio,
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(telefono.trim() ? { telefono: telefono.trim() } : {}),
        ...(nif.trim() ? { nif: nif.trim() } : {}),
        ...(zona.trim() ? { zona: zona.trim() } : {}),
        ...(notasCompletas ? { notas: notasCompletas } : {}),
      })
      .then(async (r) => {
        if (propiedadId) await api.vincularProveedor(propiedadId, r.proveedor.id);
        onHecho();
        onCerrar();
      })
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
      )
      .finally(() => setEnviando(false));
  };

  return (
    <Modal idioma={idioma} titulo={t(idioma, 'prov.nuevo')} icono="edificio" onCerrar={onCerrar}>
      <form className="formulario" style={{ maxWidth: 'none' }} onSubmit={guardar}>
        <div className="campo">
          <label htmlFor="p-nombre">{t(idioma, 'prov.empresa')}</label>
          <input
            id="p-nombre"
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            maxLength={200}
            required
          />
        </div>
        <div className="rejilla dos">
          <div className="campo">
            <label htmlFor="p-oficio">{t(idioma, 'prov.categoria')}</label>
            <select id="p-oficio" value={oficio} onChange={(e) => setOficio(e.target.value as Oficio)}>
              {OFICIOS.map((o) => (
                <option key={o} value={o}>
                  {t(idioma, `oficio.${o}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="p-zona">{t(idioma, 'proveedores.zona')}</label>
            <input id="p-zona" type="text" value={zona} onChange={(e) => setZona(e.target.value)} />
          </div>
        </div>
        <div className="rejilla dos">
          <div className="campo">
            <label htmlFor="p-contacto">{t(idioma, 'prov.contacto')}</label>
            <input
              id="p-contacto"
              type="text"
              value={contacto}
              onChange={(e) => setContacto(e.target.value)}
            />
          </div>
          <div className="campo">
            <label htmlFor="p-tel">{t(idioma, 'proveedores.telefono')}</label>
            <input id="p-tel" type="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
          </div>
        </div>
        <div className="rejilla dos">
          <div className="campo">
            <label htmlFor="p-email">{t(idioma, 'proveedores.email')}</label>
            <input id="p-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="campo">
            <label htmlFor="p-nif">{t(idioma, 'proveedores.nif')}</label>
            <input id="p-nif" type="text" value={nif} onChange={(e) => setNif(e.target.value)} />
          </div>
        </div>
        <div className="campo">
          <label htmlFor="p-notas">{t(idioma, 'prov.productos')}</label>
          <textarea
            id="p-notas"
            rows={3}
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder={t(idioma, 'prov.productosPista')}
          />
        </div>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="acceso-entrar" disabled={enviando || nombre.trim().length < 2}>
          {t(idioma, 'checklists.guardar')}
        </button>
      </form>
    </Modal>
  );
}

// --- Registrar gasto ----------------------------------------------------------

export function ModalDocumento({
  idioma,
  propiedadId,
  onHecho,
  onCerrar,
}: {
  idioma: Idioma;
  propiedadId: string;
  onHecho: () => void;
  onCerrar: () => void;
}) {
  const [titulo, setTitulo] = useState('');
  const [tipo, setTipo] = useState<TipoDocumento>('CONTRATO');
  const [fichero, setFichero] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guardar = (e: React.FormEvent) => {
    e.preventDefault();
    if (titulo.trim().length < 2 || !fichero) return;
    setEnviando(true);
    api
      .crearDocumento(propiedadId, {
        titulo: titulo.trim(),
        tipo,
        nombreFichero: fichero.name,
      })
      .then(async (r) => {
        await api.subirAdjunto('DOCUMENTO', r.documento.id, fichero);
        onHecho();
        onCerrar();
      })
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
      )
      .finally(() => setEnviando(false));
  };

  return (
    <Modal idioma={idioma} titulo={t(idioma, 'ficha.subirDocumento')} icono="documento" onCerrar={onCerrar}>
      <form className="formulario" style={{ maxWidth: 'none' }} onSubmit={guardar}>
        <div className="campo">
          <label htmlFor="d-titulo">{t(idioma, 'ficha.tituloDocumento')}</label>
          <input
            id="d-titulo"
            type="text"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            maxLength={300}
            required
          />
        </div>
        <div className="campo">
          <label htmlFor="d-tipo">{t(idioma, 'checklists.tipo')}</label>
          <select id="d-tipo" value={tipo} onChange={(e) => setTipo(e.target.value as TipoDocumento)}>
            {TIPOS_DOC.map((x) => (
              <option key={x} value={x}>
                {t(idioma, `tipoDocumento.${x}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="d-fichero">{t(idioma, 'doc.fichero')}</label>
          <input
            id="d-fichero"
            type="file"
            accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx"
            onChange={(e) => setFichero(e.target.files?.[0] ?? null)}
            required
          />
          <span className="suave" style={{ fontSize: '0.8125rem' }}>
            {fichero ? fichero.name : t(idioma, 'doc.tipos')}
          </span>
        </div>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="fila-flex" style={{ gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button type="button" className="secundario" onClick={onCerrar}>
            {t(idioma, 'app.cancelar')}
          </button>
          <button type="submit" className="acceso-entrar" disabled={enviando || !fichero}>
            <Icono nombre="importar" /> {t(idioma, 'ficha.subirDocumento')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// --- Programar visita ----------------------------------------------------------

/** El valor del desplegable que significa «sin lista, voy por algo concreto». */
const MOTIVO = 'motivo-especifico';

export function ModalVisita({
  idioma,
  propiedadId,
  usuarios,
  plantillas,
  onHecho,
  onCerrar,
}: {
  idioma: Idioma;
  propiedadId: string;
  usuarios: Array<{ id: string; nombre: string }>;
  plantillas: PlantillaResumen[];
  onHecho: () => void;
  onCerrar: () => void;
}) {
  /*
    SE ELIGE LA LISTA, NO SE ESCRIBE UN TITULO.

    El titulo se tecleaba a mano y acababa en «Visita semanal», «visita semana»
    y «Semanal» para lo mismo, sin decir que habia que comprobar al llegar. Y
    despues, al empezarla, habia que volver a elegir la plantilla en otro
    dialogo: la misma decision dos veces, y la segunda con el movil delante de
    la casa. Eligiendola aqui, el titulo es su nombre y empezar no pregunta nada.
  */
  const activas = plantillas.filter((p) => p.activo);
  const [plantillaId, setPlantillaId] = useState(activas.length === 1 ? activas[0]!.id : '');
  const [motivo, setMotivo] = useState('');
  const [detalle, setDetalle] = useState('');
  const [tipo, setTipo] = useState('VISITA');
  const [fecha, setFecha] = useState(hoy());
  const [hora, setHora] = useState('');
  const [asignado, setAsignado] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
    O SE ELIGE UNA LISTA, O SE ESCRIBE PARA QUE SE VA.

    Obligar a elegir lista dejaba fuera la mitad de las visitas reales: la que
    se programa porque el vecino aviso de una gotera, porque hay que abrirle al
    albanil, porque el cliente llega el viernes. Esas no recorren una lista y
    antes solo se podian programar sin decir una palabra de por que.
  */
  const porMotivo = plantillaId === MOTIVO;
  const listo = porMotivo ? motivo.trim().length >= 2 : plantillaId !== '';

  const guardar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!listo) return;
    setEnviando(true);
    api
      .crearEvento({
        propiedadId,
        tipo: tipo as never,
        ...(porMotivo ? { titulo: motivo.trim() } : { plantillaId }),
        ...(porMotivo && detalle.trim() ? { notas: detalle.trim() } : {}),
        fecha,
        ...(hora ? { hora } : {}),
        ...(asignado ? { asignadoUserId: asignado } : {}),
      })
      .then(() => {
        onHecho();
        onCerrar();
      })
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
      )
      .finally(() => setEnviando(false));
  };

  return (
    <Modal idioma={idioma} titulo={t(idioma, 'visita.programar')} icono="calendario" onCerrar={onCerrar}>
      <form className="formulario" style={{ maxWidth: 'none' }} onSubmit={guardar}>
        <div className="campo">
          <label htmlFor="v-plantilla">{t(idioma, 'visita.plantilla')}</label>
          <select
            id="v-plantilla"
            value={plantillaId}
            onChange={(e) => setPlantillaId(e.target.value)}
            required
          >
            <option value="">{t(idioma, 'visita.elegirPlantilla')}</option>
            {activas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
            <option value={MOTIVO}>{t(idioma, 'visita.porMotivo')}</option>
          </select>
        </div>
        {porMotivo && (
          <>
            <div className="campo">
              <label htmlFor="v-motivo">{t(idioma, 'visita.motivo')}</label>
              <input
                id="v-motivo"
                type="text"
                value={motivo}
                maxLength={200}
                required
                placeholder={t(idioma, 'visita.motivoPie')}
                onChange={(e) => setMotivo(e.target.value)}
              />
            </div>
            <div className="campo">
              <label htmlFor="v-detalle">{t(idioma, 'visita.detalle')}</label>
              <textarea
                id="v-detalle"
                rows={2}
                value={detalle}
                maxLength={2000}
                onChange={(e) => setDetalle(e.target.value)}
              />
            </div>
          </>
        )}
        <div className="rejilla dos">
          <div className="campo">
            <label htmlFor="v-tipo">{t(idioma, 'evento.tipo')}</label>
            <select id="v-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              {['VISITA', 'PREVENTIVA', 'REVISION', 'LLEGADA', 'SALIDA', 'OTRO'].map((x) => (
                <option key={x} value={x}>
                  {t(idioma, `evento.${x}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="v-asig">{t(idioma, 'evento.asignado')}</label>
            <select id="v-asig" value={asignado} onChange={(e) => setAsignado(e.target.value)}>
              <option value="">{t(idioma, 'evento.sinAsignar')}</option>
              {usuarios.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="rejilla dos">
          <div className="campo">
            <label htmlFor="v-fecha">{t(idioma, 'evento.fecha')}</label>
            <input id="v-fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
          </div>
          <div className="campo">
            <label htmlFor="v-hora">{t(idioma, 'evento.hora')}</label>
            <input id="v-hora" type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
          </div>
        </div>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="acceso-entrar" disabled={enviando || !listo}>
          {t(idioma, 'evento.guardar')}
        </button>
      </form>
    </Modal>
  );
}
